import { forwardRef, useEffect, useMemo, useRef, useImperativeHandle, useState, useCallback } from 'react';
import type Handsontable from 'handsontable';
import { HandsontableWrapperSimple, type HandsontableWrapperRef, type HandsontableWrapperSimpleProps } from '../HandsontableWrapperSimple';
import { LocalTableToolbar } from '../../tablecontrols/LocalTableToolbar';
import { ResetFiltersButton } from '../../tablecontrols/ResetFiltersButton';
import { useTableTracking, type TrackedTableProps, type TrackedTableRef } from '../tracking';
import { useIsReloading } from '../../../contexts/ToolbarContext';
import { useTrackedTableRbac } from './useTrackedTableRbac';
import { useCombinedCells } from './useCombinedCells';
import { useTrackedTableActions } from './useTrackedTableActions';
import { useTableSettings } from '../hooks/useTableSettings';
import { TableLoadingState } from './TableLoadingState';
import { EmptyAddRowOverlay } from './EmptyAddRowOverlay';
import containerStyles from '../../../styles/components/ResizableTableContainer.module.scss';

export const TrackedTable = forwardRef<TrackedTableRef, TrackedTableProps>(({
  tableId,
  baseline,
  enableTracking = true,
  enableRowTracking = false,
  trackingMode = 'auto',
  derivedCompute,
  rowIds,
  resetOnBaselineChange = false,
  autoRegisterDirty = true,
  enableHorizontalScroll = false,
  persistSettings = true,
  onTrackingChange,
  onRowOperationsChange,
  onClear,
  title,
  enableLocalToolbar = false,
  toolbar: customToolbar,
  wrapperClassName,
  wrapperStyle,
  onAfterChange: userOnAfterChange,
  afterRemoveRow: userAfterRemoveRow,
  afterCreateRow: userAfterCreateRow,
  data,
  columns,
  cells: userCells,
  loading = false,
  enableErrorBoundary = true,
  queryError = null,
  onRetry,
  emptyMessage,
  rbacResource,
  rbacEnabled = true,
  showRbacBadge = false,
  showAddRow,
  addRowTooltip,
  onRowAdded,
  localToolbarExtra,
  ...handsontableProps
}, ref) => {
  const hotTableRef = useRef<HandsontableWrapperRef>(null);
  const [isTableReady, setIsTableReady] = useState(false);
  // Строки, созданные прямо в сетке (ещё не попавшие в проп `data`).
  // Нужны, чтобы убрать оверлей пустого состояния сразу после «+».
  const [hasGridRows, setHasGridRows] = useState(false);
  const [hasActiveFilters, setHasActiveFilters] = useState(false);
  const isReloading = useIsReloading();
  const effectiveLoading = loading || isReloading;

  // ── Column settings persistence ──────────────────────────────────────
  const {
    savedColWidths,
    handleColumnResize,
    applyToInstance: applyColumnSettings,
    resetSettings: resetColumnSettingsRaw,
    hasCustomSettings,
  } = useTableSettings(tableId, persistSettings);
  const resetColumnSettings = useCallback(
    () => resetColumnSettingsRaw(hotTableRef),
    [resetColumnSettingsRaw],
  );

  const {
    finalReadOnly,
    finalColumns,
    finalContextMenu,
    titleWithRbacBadge,
  } = useTrackedTableRbac({
    rbacResource,
    rbacEnabled,
    showRbacBadge,
    columns,
    contextMenu: handsontableProps.contextMenu,
    readOnly: handsontableProps.readOnly,
    title,
  });

  const {
    tracker,
    changeVersion,
    combinedAfterChange,
    combinedAfterRemoveRow,
    combinedAfterCreateRow,
    combinedBeforeRemoveRow,
    restoreBaselineValues,
    cellsCallback,
    rowIdMappingRef,
  } = useTableTracking({
    tableId,
    baseline,
    enableTracking,
    enableRowTracking,
    trackingMode,
    derivedCompute,
    columns,
    data,
    rowIds,
    resetOnBaselineChange,
    autoRegisterDirty,
    onTrackingChange,
    onRowOperationsChange,
    hotTableRef,
    userOnAfterChange,
    userAfterRemoveRow,
    userAfterCreateRow,
  });

  const { handleClear } = useTrackedTableActions({
    tableId,
    enableTracking,
    enableLocalToolbar,
    changeVersion,
    tracker,
    restoreBaselineValues,
    onClear,
    hotTableRef,
  });

  const finalCells = useCombinedCells({
    userCells,
    cellsCallback,
    enableTracking,
    columns: finalColumns,
  });

  // ── Merge afterColumnResize / beforeRemoveRow into hotSettings ──────
  const trackRows = enableTracking || enableRowTracking;
  const mergedHotSettings = useMemo(() => {
    const userHotSettings = handsontableProps.hotSettings;
    if (!persistSettings && !trackRows) return userHotSettings;
    return {
      ...userHotSettings,
      ...(persistSettings ? {
        // Сигнатура HT: afterColumnResize(newSize, column, isDoubleClick)
        afterColumnResize: (newSize: number, column: number, isDoubleClick: boolean) => {
          handleColumnResize(newSize, column, isDoubleClick);
          const userCallback = userHotSettings?.afterColumnResize as
            | ((s: number, c: number, d: boolean) => void)
            | undefined;
          userCallback?.(newSize, column, isDoubleClick);
        },
      } : {}),
      ...(trackRows ? {
        // Снимок данных удаляемых строк ДО удаления (см. useTableTracking)
        beforeRemoveRow: ((index, amount, physicalRows, source) => {
          combinedBeforeRemoveRow(index, amount, physicalRows, source);
          const userCallback = userHotSettings?.beforeRemoveRow;
          return userCallback?.(index, amount, physicalRows, source);
        }) as NonNullable<Handsontable.GridSettings['beforeRemoveRow']>,
      } : {}),
    };
  }, [persistSettings, trackRows, handsontableProps.hotSettings, handleColumnResize, combinedBeforeRemoveRow]);

  // ── Применяем сохранённые ширины после инициализации таблицы ───────
  // Используем setManualSize() — плагин помечает колонки как «вручную изменённые»,
  // и stretchH: 'all' корректно их учитывает (не растягивает поверх).
  // Effect срабатывает ДО isTableReady (2 rAF), поэтому без мерцания.
  useEffect(() => {
    if (!savedColWidths || !persistSettings) return;
    applyColumnSettings(hotTableRef);
  }, [savedColWidths, persistSettings, applyColumnSettings]);

  const autoToolbar = useMemo(() => {
    if (!enableLocalToolbar) return null;

    return (
      <LocalTableToolbar
        tableRef={hotTableRef}
        exportFileName={tableId}
        exportSheetName="Данные"
        showAddRow={showAddRow && !finalReadOnly}
        addRowTooltip={addRowTooltip}
        onRowAdded={onRowAdded}
        onResetColumnSettings={hasCustomSettings ? resetColumnSettings : undefined}
        extra={localToolbarExtra}
      />
    );
  }, [enableLocalToolbar, tableId, showAddRow, finalReadOnly, addRowTooltip, onRowAdded, hasCustomSettings, resetColumnSettings, localToolbarExtra]);

  const finalToolbar = useMemo(() => {
    const resetFiltersButton = hasActiveFilters
      ? <ResetFiltersButton tableRef={hotTableRef} />
      : null;
    const resolvedToolbar = effectiveLoading ? null : (customToolbar || autoToolbar);
    return (resetFiltersButton || resolvedToolbar)
      ? <>{resetFiltersButton}{resolvedToolbar}</>
      : null;
  }, [hasActiveFilters, effectiveLoading, customToolbar, autoToolbar]);

  useImperativeHandle(ref, () => ({
    getHotInstance: () => hotTableRef.current?.getHotInstance?.() || null,
    updateData: (newData: (string | number | boolean | null)[][]) => hotTableRef.current?.updateData?.(newData),
    refresh: () => hotTableRef.current?.refresh?.(),

    getTrackingApi: () => enableTracking ? tracker : null,
    getChangedCells: () => tracker.getChangedCells(),
    getDeletedRows: () => tracker.getDeletedRows(),
    getAddedRows: () => tracker.getAddedRows(),
    // Геттер, а не значение: ref-объект создаётся один раз (tracker стабилен),
    // снапшот значения навсегда остался бы false.
    get hasChanges() {
      return tracker.hasChanges;
    },
    clearChanges: handleClear,
    resetBaseline: (newBaseline, newRowIds) => {
      if (newBaseline) {
        tracker.setBaseline(newBaseline);
      }
      tracker.reset();
      if (enableRowTracking && newRowIds) {
        const mapping = new Map<number, string | number>();
        newRowIds.forEach((id, index) => {
          mapping.set(index, id);
        });
        rowIdMappingRef.current = mapping;
      }
      // Handsontable автоматически перерисует ячейки через cells callback
      const hot = hotTableRef.current?.getHotInstance?.();
      if (hot) {
        hot.render();
      }
    },
    resetColumnSettings,
  }), [tracker, enableTracking, enableRowTracking, handleClear, rowIdMappingRef, resetColumnSettings]);

  // ── Пустое состояние: сетка остаётся смонтированной ─────────────────
  // Раньше при пустом `data` вместо таблицы возвращалась заглушка, и кнопка
  // «+» упиралась в отсутствующий инстанс Handsontable (тихий no-op).
  const canAddRow = !!showAddRow && !finalReadOnly;
  const needsEmptyOverlayHost = emptyMessage != null && !!showAddRow;

  // Схема строки для пустого набора данных. Без неё Handsontable определяет
  // dataType по первой строке, а на пустых данных считает набор объектным и
  // создаёт `{}` вместо массива нужной длины.
  const settingsWithRowSchema = useMemo(() => {
    if (!canAddRow) return mergedHotSettings;
    const cols = finalColumns;
    if (!Array.isArray(cols) || cols.length === 0) return mergedHotSettings;
    if (!cols.every((col) => typeof col?.data === 'number')) return mergedHotSettings;
    if (mergedHotSettings?.dataSchema) return mergedHotSettings;
    return { ...mergedHotSettings, dataSchema: new Array(cols.length).fill(null) };
  }, [canAddRow, finalColumns, mergedHotSettings]);

  const baseAfterCreateRow = trackRows ? combinedAfterCreateRow : userAfterCreateRow;
  const handleAfterCreateRow: HandsontableWrapperSimpleProps['afterCreateRow'] = useCallback((
    index: number,
    amount: number,
    source?: Handsontable.ChangeSource,
  ) => {
    setHasGridRows(true);
    baseAfterCreateRow?.(index, amount, source);
  }, [baseAfterCreateRow]);

  const baseAfterRemoveRow = trackRows ? combinedAfterRemoveRow : userAfterRemoveRow;
  const handleAfterRemoveRow: HandsontableWrapperSimpleProps['afterRemoveRow'] = useCallback((
    index: number,
    amount: number,
    physicalRows: number[],
    source?: Handsontable.ChangeSource,
  ) => {
    const hot = hotTableRef.current?.getHotInstance?.();
    setHasGridRows((hot?.countRows() ?? 0) > 0);
    baseAfterRemoveRow?.(index, amount, physicalRows, source);
  }, [baseAfterRemoveRow]);

  // Новый набор данных (перезагрузка, смена года) — локальные строки сброшены.
  useEffect(() => {
    setHasGridRows(Array.isArray(data) && data.length > 0);
  }, [data]);

  const wrapperProps: HandsontableWrapperSimpleProps = {
    ...handsontableProps,
    id: tableId,
    columns: finalColumns,
    data,
    cells: finalCells,
    hotSettings: settingsWithRowSchema,
    onAfterChange: combinedAfterChange,
    // Сдвиг ключей подсветки нужен и при чистом cell-трекинге: добавление или
    // удаление строки смещает физические индексы независимо от enableRowTracking.
    afterRemoveRow: handleAfterRemoveRow,
    afterCreateRow: handleAfterCreateRow,
    readOnly: finalReadOnly,
    contextMenu: finalContextMenu,
    onFiltersChanged: setHasActiveFilters,
  };

  // Инлайн-обёртка контейнера (бывший ResizableTableContainer)
  const wrapInContainer = useCallback((children: React.ReactNode) => {
    const containerClasses = [
      containerStyles.container,
      enableHorizontalScroll && containerStyles.horizontalScroll,
      wrapperClassName,
    ].filter(Boolean).join(' ');

    const hasHeader = titleWithRbacBadge || finalToolbar;

    return (
      <div className={containerClasses} style={wrapperStyle}>
        <div className={containerStyles.tableWrapper}>
          {hasHeader && (
            <div className={containerStyles.tableHeader}>
              {titleWithRbacBadge && <div className={containerStyles.tableTitle}>{titleWithRbacBadge}</div>}
              {finalToolbar && <div className={containerStyles.tableExtra}>{finalToolbar}</div>}
            </div>
          )}
          <div className={containerStyles.tableBody}>
            {children}
          </div>
        </div>
      </div>
    );
  }, [enableHorizontalScroll, wrapperClassName, wrapperStyle, titleWithRbacBadge, finalToolbar]);

  const showContainer = !!(titleWithRbacBadge || finalToolbar);

  useEffect(() => {
    if (data && !effectiveLoading && !queryError) {
      let innerFrameId: number;
      const outerFrameId = requestAnimationFrame(() => {
        innerFrameId = requestAnimationFrame(() => {
          setIsTableReady(true);
        });
      });
      return () => {
        cancelAnimationFrame(outerFrameId);
        cancelAnimationFrame(innerFrameId);
      };
    }
  }, [data, effectiveLoading, queryError]);

  const isEmpty = emptyMessage != null && Array.isArray(data) && data.length === 0 && !effectiveLoading && !queryError;
  // Пустую сетку показываем только там, где из неё можно создать строку.
  // В остальных случаях остаётся прежняя заглушка вместо таблицы.
  const showEmptyOverlay = isEmpty && canAddRow && !hasGridRows;

  if (isEmpty && !canAddRow) {
    const emptyContent = (
      <div style={{ textAlign: 'center', padding: '40px', color: 'var(--color-text-secondary)' }}>
        {emptyMessage}
      </div>
    );
    return showContainer ? wrapInContainer(emptyContent) : emptyContent;
  }

  if (queryError) {
    const errorContent = (
      <TableLoadingState
        loading={false}
        isTableReady={true}
        queryError={queryError}
        height={handsontableProps.height}
        enableErrorBoundary={false}
        tableId={tableId}
        tableTitle={typeof title === 'string' ? title : tableId}
        onRetry={onRetry}
      >
        {null}
      </TableLoadingState>
    );

    return showContainer ? wrapInContainer(errorContent) : errorContent;
  }

  const tableContent = (
    <TableLoadingState
      loading={effectiveLoading}
      isTableReady={isTableReady}
      queryError={null}
      height={handsontableProps.height}
      enableErrorBoundary={enableErrorBoundary}
      tableId={tableId}
      tableTitle={typeof title === 'string' ? title : undefined}
      onRetry={onRetry}
    >
      <HandsontableWrapperSimple
        ref={hotTableRef}
        {...wrapperProps}
      />
    </TableLoadingState>
  );

  // Обёртка держится на стабильных пропах (emptyMessage + showAddRow), а не на
  // showEmptyOverlay: иначе появление первой строки меняло бы позицию сетки в
  // дереве React, Handsontable размонтировался бы и добавленная строка терялась.
  const body = needsEmptyOverlayHost ? (
    <div className={containerStyles.emptyOverlayHost}>
      {tableContent}
      {showEmptyOverlay && isTableReady && (
        <EmptyAddRowOverlay
          tableRef={hotTableRef}
          message={emptyMessage}
          label={addRowTooltip}
          onRowAdded={onRowAdded}
        />
      )}
    </div>
  ) : tableContent;

  return showContainer ? wrapInContainer(body) : body;
});

TrackedTable.displayName = 'TrackedTable';

export default TrackedTable;
